import { useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';

export interface PagePreviewDialogProps {
  /** Original PDF page number to preview, or null to keep the dialog closed. */
  pageNumber: number | null;
  pageCount: number;
  /** Page image URL keyed by ORIGINAL PDF page number (1-indexed). */
  pageImages: Record<number, string | undefined>;
  imagesLoading?: boolean;
  rotation: number;
  onRotate: (delta: -90 | 90) => void;
  /** Navigate to a different page within the dialog. */
  onNavigate: (next: number) => void;
  onClose: () => void;
  /** Whether a child boundary is set immediately after this page's position. */
  boundaryAfterActive?: boolean;
  onToggleBoundaryAfter?: () => void;
  /** Hides the boundary toggle for the last visible page. */
  isLastVisible?: boolean;
  /** 1-based child index this page belongs to, for header context. */
  childIndex?: number;
  childCount?: number;
  /** Whether this page is flagged for omission. */
  omitted?: boolean;
  onToggleOmit?: () => void;
  /** Whether this page is designated as a cover page. */
  isCover?: boolean;
  onToggleCover?: () => void;
}

/**
 * Modal preview of a single page on the split route. Opened from the view
 * (eye) button on a tile to inspect a page without leaving the grid. Rotates
 * with the same rotation map the grid uses. Left/right arrow keys page through
 * the document while open.
 *
 * Ported from glynt-ui-next's PagePreviewDialog: shadcn Dialog → MUI Dialog,
 * lucide icons → MUI icons, per-page image fetch → image URL from the parent.
 */
export function PagePreviewDialog({
  pageNumber,
  pageCount,
  pageImages,
  imagesLoading = false,
  rotation,
  onRotate,
  onNavigate,
  onClose,
  boundaryAfterActive,
  onToggleBoundaryAfter,
  isLastVisible,
  childIndex,
  childCount,
  omitted,
  onToggleOmit,
  isCover,
  onToggleCover,
}: PagePreviewDialogProps) {
  const open = pageNumber !== null;
  const canPrev = pageNumber !== null && pageNumber > 1;
  const canNext = pageNumber !== null && pageNumber < pageCount;

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft' && canPrev) {
        e.preventDefault();
        onNavigate(pageNumber! - 1);
      } else if (e.key === 'ArrowRight' && canNext) {
        e.preventDefault();
        onNavigate(pageNumber! + 1);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, pageNumber, canPrev, canNext, onNavigate]);

  const isQuarter = rotation === 90 || rotation === 270;
  const imageUrl = pageNumber !== null ? pageImages[pageNumber] : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: 'min(92vw, 720px)', maxHeight: '90vh', m: 1 } }}
    >
      {pageNumber !== null ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              py: 1,
              pl: 2,
              pr: 6,
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
              Page {pageNumber} of {pageCount}
              {rotation ? ` · ${rotation}°` : ''}
            </Typography>
            {omitted ? (
              <Badge color="error.main" contrast="error.contrastText">
                Omitted
              </Badge>
            ) : null}
            {isCover ? (
              <Badge color="primary.main" contrast="primary.contrastText" cy="preview-cover-badge">
                Cover
              </Badge>
            ) : null}
            {childIndex !== undefined && childCount !== undefined && childCount > 1 ? (
              <Box
                sx={{
                  borderRadius: 0.5,
                  px: 0.75,
                  py: 0.25,
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'primary.main',
                  backgroundColor: (t) => `${t.palette.primary.main}26`,
                }}
              >
                Child {childIndex} of {childCount}
              </Box>
            ) : null}
          </Box>

          <DialogContent sx={{ p: 0, position: 'relative', overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxHeight: '80vh',
                overflow: 'auto',
                bgcolor: 'action.hover',
                p: 2,
              }}
            >
              {imagesLoading ? (
                <Skeleton variant="rectangular" width="100%" height="70vh" />
              ) : !imageUrl ? (
                <Typography variant="body2" color="text.secondary">
                  No preview available.
                </Typography>
              ) : (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={`Page ${pageNumber}`}
                  data-cy="page-preview-image"
                  data-rotation={rotation}
                  sx={{
                    maxHeight: '78vh',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    transform: rotation
                      ? `rotate(${rotation}deg)${isQuarter ? ' scale(0.78)' : ''}`
                      : undefined,
                    transformOrigin: 'center center',
                    transition: 'transform 150ms ease',
                  }}
                />
              )}
            </Box>

            {/* Prev / next arrows */}
            <IconButton
              onClick={() => canPrev && onNavigate(pageNumber - 1)}
              disabled={!canPrev}
              aria-label="Previous page"
              title="Previous page"
              data-cy="preview-prev"
              sx={navArrowSx('left')}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => canNext && onNavigate(pageNumber + 1)}
              disabled={!canNext}
              aria-label="Next page"
              title="Next page"
              data-cy="preview-next"
              sx={navArrowSx('right')}
            >
              <ChevronRightIcon />
            </IconButton>

            {/* Action toolbar */}
            <Box
              sx={{
                position: 'absolute',
                insetInline: 0,
                bottom: 12,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  px: 1,
                  py: 0.5,
                  boxShadow: 3,
                }}
              >
                <IconButton size="small" onClick={() => onRotate(-90)} aria-label="Rotate page left" title="Rotate page left">
                  <RotateLeftIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onRotate(90)} aria-label="Rotate page right" title="Rotate page right">
                  <RotateRightIcon fontSize="small" />
                </IconButton>

                {onToggleBoundaryAfter && !isLastVisible && !isCover ? (
                  <>
                    <Divider />
                    <Button
                      size="small"
                      variant={boundaryAfterActive ? 'contained' : 'text'}
                      color={boundaryAfterActive ? 'primary' : 'inherit'}
                      startIcon={<ContentCutIcon sx={{ fontSize: 16 }} />}
                      onClick={onToggleBoundaryAfter}
                      aria-pressed={boundaryAfterActive}
                      data-cy="preview-boundary-toggle"
                      title={
                        boundaryAfterActive
                          ? 'Remove boundary after this page'
                          : 'Insert boundary after this page'
                      }
                      sx={{ textTransform: 'none' }}
                    >
                      {boundaryAfterActive ? 'Boundary on' : 'Boundary after'}
                    </Button>
                  </>
                ) : null}

                {onToggleCover && !omitted ? (
                  <>
                    <Divider />
                    <Button
                      size="small"
                      variant={isCover ? 'contained' : 'text'}
                      color={isCover ? 'primary' : 'inherit'}
                      startIcon={isCover ? <StarBorderIcon sx={{ fontSize: 16 }} /> : <StarIcon sx={{ fontSize: 16 }} />}
                      onClick={onToggleCover}
                      aria-pressed={isCover}
                      data-cy="preview-cover-toggle"
                      title={
                        isCover
                          ? 'Remove from cover pages'
                          : 'Designate as cover page (prepended to every child)'
                      }
                      sx={{ textTransform: 'none' }}
                    >
                      {isCover ? 'Unmark cover' : 'Cover page'}
                    </Button>
                  </>
                ) : null}

                {onToggleOmit && !isCover ? (
                  <>
                    <Divider />
                    <Button
                      size="small"
                      variant={omitted ? 'contained' : 'text'}
                      color={omitted ? 'error' : 'inherit'}
                      startIcon={omitted ? <RestoreIcon sx={{ fontSize: 16 }} /> : <BlockIcon sx={{ fontSize: 16 }} />}
                      onClick={onToggleOmit}
                      aria-pressed={omitted}
                      data-cy="preview-omit-toggle"
                      title={
                        omitted
                          ? 'Restore this page to the output'
                          : 'Omit this page from every child'
                      }
                      sx={{ textTransform: 'none' }}
                    >
                      {omitted ? 'Restore' : 'Omit page'}
                    </Button>
                  </>
                ) : null}
              </Box>
            </Box>
          </DialogContent>
        </>
      ) : null}
    </Dialog>
  );
}

function navArrowSx(side: 'left' | 'right') {
  return {
    position: 'absolute' as const,
    [side]: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 40,
    height: 40,
    bgcolor: 'background.paper',
    boxShadow: 2,
    '&:hover': { bgcolor: 'grey.100' },
  };
}

function Divider() {
  return <Box aria-hidden sx={{ mx: 0.5, height: 16, width: '1px', bgcolor: 'divider' }} />;
}

function Badge({
  children,
  color,
  contrast,
  cy,
}: {
  children: React.ReactNode;
  color: string;
  contrast: string;
  cy?: string;
}) {
  return (
    <Box
      data-cy={cy}
      sx={{
        borderRadius: 0.5,
        px: 0.75,
        py: 0.25,
        fontSize: 12,
        fontWeight: 600,
        bgcolor: color,
        color: contrast,
      }}
    >
      {children}
    </Box>
  );
}
